/**
 * ChallengeModeView — Strategic challenge cards for the canvas.
 *
 * Evaluates rules against current canvas state and shows severity-colored
 * challenge cards. Each card links to Guided Mode for the affected section.
 */

import { AlertCircle, AlertTriangle, Lightbulb, Compass, ArrowRight } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { useCanvasInteractive } from './CanvasInteractiveContext'
import { useCanvasChallenges } from '@/hooks/useCanvasChallenges'
import { alpha } from '@/components/canvas/canvasTheme'

const SEVERITY_CONFIG = {
  critical: {
    icon: AlertCircle,
    label: 'Critical',
    bgColor: 'var(--color-danger-bg)',
    borderColor: 'var(--color-danger)',
    textColor: 'var(--color-danger-fg)',
    badgeBg: 'var(--color-danger)',
  },
  strategic: {
    icon: AlertTriangle,
    label: 'Strategic',
    bgColor: 'var(--color-warning-bg)',
    borderColor: 'var(--color-warning)',
    textColor: 'var(--color-warning-fg)',
    badgeBg: 'var(--color-warning)',
  },
  opportunity: {
    icon: Lightbulb,
    label: 'Opportunity',
    bgColor: 'var(--color-success-bg)',
    borderColor: 'var(--color-success)',
    textColor: 'var(--color-success-fg)',
    badgeBg: 'var(--color-success)',
  },
}

export function ChallengeModeView() {
  const {
    interactiveConfig,
    sectionStats,
    sectionItems,
    selectedTemplate,
    navigateToGuidedSection,
  } = useCanvasInteractive()

  const { challenges, criticalCount, strategicCount, opportunityCount } = useCanvasChallenges({
    rules: interactiveConfig.challengeRules,
    sectionStats,
    sectionItems,
  })

  // Find section for display
  const getSectionTitle = (sectionId: string) => {
    return selectedTemplate.sections.find((s) => s.id === sectionId)?.title || sectionId
  }

  const getSectionColor = (sectionId: string) => {
    return selectedTemplate.sections.find((s) => s.id === sectionId)?.color || '#8a847a'
  }

  if (challenges.length === 0) {
    return (
      <div className="flex flex-col items-center justify-center h-full py-16">
        <div className="w-12 h-12 rounded-full bg-market/10 flex items-center justify-center mb-4">
          <Lightbulb className="w-6 h-6 text-market" />
        </div>
        <h3 className="text-lg font-display font-semibold text-text mb-2">
          No challenges detected
        </h3>
        <p className="text-sm text-text-secondary text-center max-w-sm">
          Your canvas is well-balanced. Keep filling in sections to maintain coverage across all
          areas of your business model.
        </p>
      </div>
    )
  }

  return (
    <div className="p-4 overflow-auto">
      {/* Summary bar */}
      <div className="flex items-center gap-3 mb-4 px-4 py-2.5 rounded-lg bg-warm-100 border border-border-subtle">
        <Lightbulb className="w-4 h-4 text-text-secondary flex-shrink-0" />
        <span className="text-xs text-text-secondary">
          {challenges.length} challenge{challenges.length !== 1 ? 's' : ''} found
        </span>
        <div className="flex items-center gap-2 ml-auto">
          {criticalCount > 0 && (
            <span className="text-xs px-2 py-0.5 rounded-full bg-danger-bg text-danger-fg">
              {criticalCount} critical
            </span>
          )}
          {strategicCount > 0 && (
            <span className="text-xs px-2 py-0.5 rounded-full bg-warning-bg text-warning-fg">
              {strategicCount} strategic
            </span>
          )}
          {opportunityCount > 0 && (
            <span className="text-xs px-2 py-0.5 rounded-full bg-success-bg text-success-fg">
              {opportunityCount} opportunity
            </span>
          )}
        </div>
      </div>

      {/* Challenge cards */}
      <div className="flex flex-col gap-3">
        {challenges.map((challenge) => {
          const config = SEVERITY_CONFIG[challenge.severity]
          const SeverityIcon = config.icon
          const sectionColor = getSectionColor(challenge.affectedSection)

          return (
            <div
              key={challenge.id}
              className="rounded-xl overflow-hidden"
              style={{
                backgroundColor: config.bgColor,
                border: `1px solid ${config.borderColor}`,
              }}
            >
              <div className="p-4">
                {/* Header */}
                <div className="flex items-start gap-3 mb-2">
                  <SeverityIcon
                    className="w-4 h-4 flex-shrink-0 mt-0.5"
                    style={{ color: config.textColor }}
                  />
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 mb-1">
                      <h3
                        className="text-sm font-semibold"
                        style={{ color: config.textColor }}
                      >
                        {challenge.title}
                      </h3>
                      <span
                        className="text-[10px] font-medium px-1.5 py-0.5 rounded-full text-white"
                        style={{ backgroundColor: config.badgeBg }}
                      >
                        {config.label}
                      </span>
                    </div>
                    <p className="text-xs text-text-secondary leading-relaxed">
                      {challenge.description}
                    </p>
                  </div>
                </div>

                {/* Footer: affected section + action */}
                <div className="flex items-center justify-between mt-3 pt-3 border-t" style={{ borderColor: alpha(sectionColor, 0.2) }}>
                  <div
                    className="flex items-center gap-1.5 px-2 py-0.5 rounded-md text-xs"
                    style={{
                      backgroundColor: alpha(sectionColor, 0.1),
                      color: sectionColor,
                    }}
                  >
                    Affects: {getSectionTitle(challenge.affectedSection)}
                  </div>
                  <Button
                    variant="ghost"
                    size="sm"
                    className="text-xs h-7 gap-1"
                    style={{ color: config.textColor }}
                    onClick={() => navigateToGuidedSection(challenge.affectedSection)}
                  >
                    <Compass className="w-3 h-3" />
                    Solve with guided mode
                    <ArrowRight className="w-3 h-3" />
                  </Button>
                </div>
              </div>
            </div>
          )
        })}
      </div>
    </div>
  )
}
